import React, {useState} from "react";
import {observer} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {Modal, Tag} from "antd";
import moment from "moment";
import {
    DetailModalContainer,
    DetailTitle,
    InfoGridContainer,
    InfoCard,
    InfoCardHeader,
    InfoCardIcon,
    InfoCardTitle,
    InfoItem,
    InfoLabel,
    InfoValue,
    StatusTag
} from "../style/CustomerRequestsPage.styles";
import {ButtonContainer, CancelButton} from "./CourierRequestConfirmationModal.styles";
import {useStore} from "hooks/useStore";
import RequestCloseReasonModal from "./RequestCloseReasonModal";
import {shiftRequestStatusEnums} from "constants/constants";

const RequestDetailModal = ({visible, onClose, requestData, refetchTable}) => {
    const {t} = useTranslation();
    const stores = useStore();
    const [showCloseReasonModal, setShowCloseReasonModal] = useState(false);
    const [cancelRequestLoading, setCancelRequestLoading] = useState(false);

    if (!requestData) return null;

    const getStatusText = status => {
        const statusMap = {
            all: t("screens.customerRequests.all"),
            waiting: t("screens.customerRequests.waiting"),
            pending_approval: t("screens.customerRequests.pendingApproval"),
            recruitment_in_progress: t("screens.customerRequests.recruitmentInProgress"),
            recruitment_completed: t("screens.customerRequests.recruitmentCompleted"),
            completed: t("screens.customerRequests.completed"),
            cancelled: t("screens.customerRequests.cancelled"),
            rejected: t("screens.customerRequests.rejected"),
            approved: t("screens.customerRequests.approved")
        };
        return statusMap[status] || status;
    };

    const getStatusColor = status => {
        const colorMap = {
            created: "blue",
            waiting: "#808080",
            pending_approval: "#faad14",
            recruitment_in_progress: "#1890ff",
            recruitment_completed: "#722ed1",
            completed: "#52c41a",
            cancelled: "#d9d9d9",
            rejected: "#ff4d4f",
            approved: "#52c41a"
        };
        return colorMap[status] || "default";
    };

    const getRequestTypeText = (type, subType) => {
        if (type === "support") return t("screens.customerRequests.support");
        if (type === "planning") {
            return subType === "increment"
                ? t("screens.customerRequests.increase")
                : t("screens.customerRequests.decrease");
        }
        return t("screens.customerRequests.planning");
    };

    const getTotalHours = (startHour, endHour) => {
        if (!startHour || !endHour) return 0;
        const start = parseInt(startHour.split(":")[0]);
        const end = parseInt(endHour.split(":")[0]);
        if (end > start) {
            return end - start;
        } else if (end < start) {
            return 24 - start + end;
        }
        return 0;
    };

    const getTotalDays = (startDate, endDate) => {
        if (!startDate || !endDate) return 0;
        const start = moment(startDate).startOf("day");
        const end = moment(endDate).startOf("day");
        return end.diff(start, "days") + 1;
    };

    const handleCloseRequest = () => {
        setShowCloseReasonModal(true);
    };

    const handleCloseReasonConfirm = reasonData => {
        const reasons = reasonData.reasons.map(reason => {
            if (reasonData.otherReason) {
                return `${reason} - ${reasonData.otherReason}`;
            }
            return reason;
        });

        const cancelReason = reasons.join(", ");

        setCancelRequestLoading(true);

        stores.shiftRequestStore
            .cancelShiftRequest({
                id: requestData?._id,
                cancelReason: cancelReason
            })
            .then(() => {
                setShowCloseReasonModal(false);
                onClose();
                if (refetchTable) {
                    refetchTable();
                }
            })
            .catch(error => {
                console.error("Error closing request:", error);
            })
            .finally(() => {
                setCancelRequestLoading(false);
            });
    };

    const handleCloseReasonCancel = () => {
        setShowCloseReasonModal(false);
    };

    return (
        <>
            <Modal
                title={<DetailTitle>{t("screens.customerRequests.requestDetails")}</DetailTitle>}
                visible={visible}
                onCancel={onClose}
                footer={null}
                width={1000}
                centered
                bodyStyle={{overflowY: "scroll"}}
            >
                <DetailModalContainer>
                    <InfoGridContainer>
                        {/* Request Information Card */}
                        <InfoCard>
                            <InfoCardHeader>
                                <InfoCardTitle>
                                    {t("screens.customerRequests.requestInfo")}
                                </InfoCardTitle>
                            </InfoCardHeader>
                            <InfoItem>
                                <InfoLabel>{t("screens.customerRequests.requestNumber")}</InfoLabel>
                                <InfoValue>
                                    {requestData._id?.substring(0, 8)?.toUpperCase()}
                                </InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t("screens.customerRequests.requestType")}</InfoLabel>
                                <InfoValue>
                                    {getRequestTypeText(requestData.type, requestData.subType)}
                                </InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t("screens.customerRequests.status")}</InfoLabel>
                                <InfoValue>
                                    <StatusTag color={getStatusColor(requestData.status)}>
                                        {getStatusText(requestData.status)}
                                    </StatusTag>
                                </InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t("screens.customerRequests.requestDate")}</InfoLabel>
                                <InfoValue>
                                    {moment(requestData.createdAt).format("DD.MM.YYYY HH:mm")}
                                </InfoValue>
                            </InfoItem>
                        </InfoCard>

                        {/* Date & Time Information Card */}
                        <InfoCard>
                            <InfoCardHeader>
                                <InfoCardTitle>
                                    {t("screens.customerRequests.dateTimeInfo")}
                                </InfoCardTitle>
                            </InfoCardHeader>
                            <InfoItem>
                                <InfoLabel>{t("screens.customerRequests.startDate")}</InfoLabel>
                                <InfoValue>
                                    {moment(requestData.startDate).format("DD.MM.YYYY")}
                                </InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t("screens.customerRequests.endDate")}</InfoLabel>
                                <InfoValue>
                                    {moment(requestData.endDate).format("DD.MM.YYYY")}
                                </InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t("screens.customerRequests.startTime")}</InfoLabel>
                                <InfoValue>{requestData.startHour}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t("screens.customerRequests.endTime")}</InfoLabel>
                                <InfoValue>{requestData.endHour}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t("screens.customerRequests.day")}</InfoLabel>
                                <InfoValue>
                                    {getTotalDays(requestData.startDate, requestData.endDate)}{" "}
                                    {t("screens.customerRequests.dayUnit")}
                                </InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t("screens.customerRequests.hour")}</InfoLabel>
                                <InfoValue>
                                    {getTotalHours(requestData.startHour, requestData.endHour)}{" "}
                                    {t("screens.customerRequests.hourUnit")}
                                </InfoValue>
                            </InfoItem>
                        </InfoCard>

                        {/* Resource Information Card */}
                        <InfoCard>
                            <InfoCardHeader>
                                <InfoCardTitle>
                                    {t("screens.customerRequests.resourceInfo")}
                                </InfoCardTitle>
                            </InfoCardHeader>
                            <InfoItem>
                                <InfoLabel>{t("screens.customerRequests.courierCount")}</InfoLabel>
                                <InfoValue>
                                    {requestData.courierCount}{" "}
                                    {t("screens.customerRequests.courierUnit")}
                                </InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t("screens.customerRequests.packageCount")}</InfoLabel>
                                <InfoValue>
                                    {requestData.packageCount || "0"}{" "}
                                    {t("screens.customerRequests.packageUnit")}
                                </InfoValue>
                            </InfoItem>
                        </InfoCard>
                    </InfoGridContainer>
                    {requestData?.status !== shiftRequestStatusEnums.CANCELLED &&
                        requestData?.status !== shiftRequestStatusEnums.COMPLETED &&
                        requestData?.status !== shiftRequestStatusEnums.REJECTED && (
                            <InfoGridContainer>
                                <ButtonContainer>
                                    <CancelButton
                                        onClick={handleCloseRequest}
                                        style={{
                                            backgroundColor: "#ff4d4f",
                                            color: "#000",
                                            border: "none"
                                        }}
                                    >
                                        {t("screens.customerRequests.close")}
                                    </CancelButton>
                                </ButtonContainer>
                            </InfoGridContainer>
                        )}
                </DetailModalContainer>
            </Modal>

            <RequestCloseReasonModal
                visible={showCloseReasonModal}
                onClose={handleCloseReasonCancel}
                onConfirm={handleCloseReasonConfirm}
                loading={cancelRequestLoading}
            />
        </>
    );
};

export default observer(RequestDetailModal);
